'use client'

import { usePathname } from 'next/navigation'
import { ArrowUpRight, Mail, MapPin, Phone } from 'lucide-react'
import { navigateUrl } from '@/utils/client-request'
import { getVendorPhones } from '@/utils/vendorPhones'

export default function Contact({ vendor }: { vendor?: any }) {
  const pathname = usePathname() || ''
  const phones = getVendorPhones(vendor)
  const vendorName = vendor?.name || 'Дэлгүүр'

  const sections: Array<{ id: string, title: string, body: React.ReactNode, action?: { label: string, href: string, icon: any, external?: boolean } }> = []

  if (phones.length > 0) {
    sections.push({
      id: 'phone',
      title: 'Тусламж хэрэгтэй юу?',
      body: (
        <>
          Захиалга, төлбөр, хүргэлт, буцаалттай холбоотой асуултанд хамгийн хурдан хариулт авах арга бол шууд утсаар холбогдох юм. Та манай {phones.map((p, i) => (<span key={p}>{i > 0 && ', '}<a href={`tel:${p}`} className="underline underline-offset-4 font-semibold text-foreground">{p}</a></span>))} дугаараар холбогдоно уу.
        </>
      ),
      action: { label: 'Залгах', href: `tel:${phones[0]}`, icon: Phone },
    })
  }

  if (vendor?.email) {
    sections.push({
      id: 'email',
      title: 'Имэйлээр холбогдох',
      body: (
        <>
          Санал хүсэлт, захиалгын талаарх мэдээлэл эсвэл хамтын ажиллагааны санал авах бол <a href={`mailto:${vendor.email}`} className="underline underline-offset-4 font-semibold text-foreground">{vendor.email}</a> хаягт бичнэ үү. Бид тань руу ажлын 1-3 хоногт хариу өгөх болно.
        </>
      ),
      action: { label: 'Мэйл илгээх', href: `mailto:${vendor.email}`, icon: Mail },
    })
  }

  if (vendor?.address) {
    sections.push({
      id: 'address',
      title: 'Хаяг байршил',
      body: (
        <>
          Биечлэн ирж бараа үзэх, эсвэл захиалгаа авах боломжтой. Дэлгүүрийн хаяг: <span className="font-semibold text-foreground">{vendor.address}</span>{vendor?.addressLandmark ? <>. <span className="text-foreground/55">{vendor.addressLandmark}</span></> : null}.
        </>
      ),
      action: {
        label: 'Газрын зураг',
        href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(vendor.address)}`,
        icon: MapPin,
        external: true,
      },
    })
  }

  sections.push({
    id: 'terms',
    title: 'Үйлчилгээний нөхцөл',
    body: (
      <>
        Дэлгүүрийн үйлчилгээ, төлбөр, буцаалт, хүргэлтийн нөхцлүүдийг <a href={navigateUrl('/service', pathname)} className="underline underline-offset-4 font-semibold text-foreground">энд</a> уншиж болно.
      </>
    ),
  })

  return (
    <section className="bg-cream pb-20">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 pt-6">
        <nav className="text-[13px] text-foreground/55 mb-6">
          <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Холбоо барих</span>
        </nav>
      </div>

      <div className="relative w-full bg-[#FFD9E7] overflow-hidden">
        <div className="relative mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 py-20 sm:py-28 lg:py-32">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/55 mb-5">
            {vendorName}
          </div>
          <h1 className="font-bold font-gilroy tracking-[-0.025em] leading-[0.95] text-foreground" style={{ fontSize: 'clamp(2.75rem, 8vw, 6.5rem)' }}>
            Тусламж<br />хэрэгтэй юу?
          </h1>
        </div>
      </div>

      <div className="mx-auto max-w-230 px-4 sm:px-6 lg:px-10 pt-16">
        <div className="space-y-12 sm:space-y-14">
          {sections.map(s => (
            <article key={s.id} className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 sm:gap-8 items-start">
              <div>
                <h2 className="font-bold font-gilroy tracking-[-0.025em] leading-none text-foreground" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)' }}>
                  {s.title}
                </h2>
                <p className="mt-4 text-[15px] sm:text-[16px] leading-[1.65] text-foreground/65 max-w-2xl">
                  {s.body}
                </p>
              </div>
              {s.action && (
                <a
                  href={s.action.href}
                  target={s.action.external ? '_blank' : undefined}
                  rel={s.action.external ? 'noopener noreferrer' : undefined}
                  className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-foreground text-cream text-[13.5px] font-semibold hover:bg-foreground/90 transition-colors self-start sm:self-end whitespace-nowrap"
                >
                  <s.action.icon className="size-4" /> {s.action.label} <ArrowUpRight className="size-3.5" />
                </a>
              )}
            </article>
          ))}
        </div>
      </div>

    </section>
  )
}
